import { Delete, Edit } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React from 'react';
import { AI_FEATURES } from './AIFeatureCheckboxes';
import ActionMenu from '../../ui/primitives/ActionMenu';

// Must match backend PROVIDER_DEFAULTS keys: gemini | openai | anthropic
export const LLM_OPTIONS = [
  { id: 'gemini',    label: 'Gemini'  },
  // { id: 'openai',    label: 'ChatGPT' },
  // { id: 'anthropic', label: 'Claude'  },
];

const enabledFeatureLabels = (features = []) =>
  AI_FEATURES.filter((f) => {
    const v = features?.find((feat) => feat.featureKey === f.id);
    return v?.isEnabled ?? false;
  }).map((f) => f.label);

const ProjectsCell = ({ entry, projects }) => {
  if (entry.applyTo === 'org') {
    return (
      <Chip label="Organisation-wide" size="small" color="secondary" variant="outlined" sx={{ fontSize: '11px', height: 20 }} />
    );
  }

  const ids = entry.projectIds ?? [];

  if (ids.length === projects.length && projects.length > 0) {
    return (
      <Chip label="All projects" size="small" color="primary" variant="outlined" sx={{ fontSize: '11px', height: 20 }} />
    );
  }

  const names = ids.map((id) => projects.find((p) => p.id === id)?.name).filter(Boolean);

  if (names.length === 0) return <Typography variant="caption" color="text.disabled">—</Typography>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
      {names.map((name) => (
        <Chip key={name} label={name} size="small" variant="outlined" sx={{ fontSize: '11px', height: 20 }} />
      ))}
    </Box>
  );
};

const AIKeyTable = ({
  entries = [],
  onEdit,
  onDelete,
  showProject = false,
  projects = [],
  emptyLabel = 'No API keys configured yet.',
  onAdd,
}) => {
  const canEdit    = typeof onEdit   === 'function';
  const canDelete  = typeof onDelete === 'function';
  const hasActions = canEdit || canDelete;

  const headers = [
    'Model',
    'Key',
    ...(showProject ? ['Projects'] : []),
    'Features',
    ...(hasActions ? [''] : []),
  ];

  if (entries.length === 0) {
    return (
      <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1.5, py: 5, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {emptyLabel}{' '}
          {onAdd && (
            <Button
              size="small"
              onClick={onAdd}
              sx={{ p: 0, minWidth: 0, fontWeight: 600, fontSize: 'inherit', verticalAlign: 'baseline' }}
            >
              Add one now
            </Button>
          )}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: '#fafafa' }}>
            {headers.map((h, i) => (
              <TableCell
                key={i}
                align={h === '' ? 'right' : 'left'}
                sx={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  py: 1.25,
                  whiteSpace: 'nowrap',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {entries.map((entry) => {
            const featureLabels = enabledFeatureLabels(entry.features);
            const llmLabel      = LLM_OPTIONS.find((l) => l.id === entry.llm)?.label ?? entry.llm;
            const isDisabled    = entry.isEnabled === false;

            const menuItems = [
              {
                label: 'Edit',
                icon: <Edit />,
                onClick: () => onEdit(entry),
                disabled: isDisabled,
                hidden: !canEdit,
              },
              {
                label: 'Delete',
                icon: <Delete />,
                onClick: () => onDelete(entry.id),
                color: 'error',
                hidden: !canDelete,
              },
            ];

            return (
              <TableRow
                key={entry.id}
                sx={{ '&:last-child td': { borderBottom: 0 }, '&:hover': { bgcolor: '#fafafa' } }}
              >
                <TableCell sx={{ py: 1.5, verticalAlign: 'top', whiteSpace: 'nowrap', opacity: isDisabled ? 0.45 : 1 }}>
                  <Chip label={llmLabel} size="small" color="primary" sx={{ fontSize: '11px', height: 22, fontWeight: 600 }} />
                </TableCell>

                <TableCell sx={{ py: 1.5, verticalAlign: 'top', minWidth: 160, opacity: isDisabled ? 0.45 : 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'primary.main', fontWeight: 600 }}>
                    {entry.apiKey || '—'}
                  </Typography>
                </TableCell>

                {showProject && (
                  <TableCell sx={{ py: 1.5, verticalAlign: 'top', opacity: isDisabled ? 0.45 : 1 }}>
                    <ProjectsCell entry={entry} projects={projects} />
                  </TableCell>
                )}

                <TableCell sx={{ py: 1.5, verticalAlign: 'top', opacity: isDisabled ? 0.45 : 1 }}>
                  {featureLabels.length === 0 ? (
                    <Typography variant="caption" color="text.disabled">None</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                      {featureLabels.map((label) => (
                        <Chip key={label} label={label} size="small" variant="outlined" color="success" sx={{ fontSize: '11px', height: 20 }} />
                      ))}
                    </Box>
                  )}
                </TableCell>

                {hasActions && (
                  <TableCell sx={{ py: 1.5, verticalAlign: 'middle' }} align="right">
                    <ActionMenu
                      items={menuItems}
                      tooltip={isDisabled ? 'Enable from the project configuration to edit' : 'Actions'}
                      disabled={false}
                    />
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export default AIKeyTable;